import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import UniversalSearch from "./UniversalSearch";
import LanguageSelector from "./LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Wrench,
  LayoutDashboard,
  ClipboardList,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Building,
  ChevronDown,
  Package,
  BarChart3,
  UserCircle,
  Shield,
  CreditCard,
  Smartphone,
  Monitor,
  CalendarDays,
  QrCode,
} from "lucide-react";

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, logout, isAdmin, isImpersonating, exitImpersonation } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check enabled modules from tenant
  const enabledModules = tenant?.enabled_modules || {};
  const hasMobileTrading = enabledModules.mobile_phone_trading !== false;
  const hasITEquipment = enabledModules.it_equipment_trading !== false;
  const hasOnlineBooking = tenant?.appointment_settings?.is_booking_enabled === true;

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    { name: "Jobs", href: "/jobs", icon: ClipboardList },
    { name: "QR Intake", href: "/customer-intakes", icon: QrCode },
    ...(hasOnlineBooking ? [{ name: "Bookings", href: "/bookings", icon: CalendarDays }] : []),
    { name: "Customers", href: "/customers", icon: UserCircle },
    { name: "Inventory", href: "/inventory", icon: Package },
    ...(hasMobileTrading ? [{ name: "Mobile Trading", href: "/mobile-trading", icon: Smartphone }] : []),
    ...(hasITEquipment ? [{ name: "IT Equipment", href: "/it-equipment", icon: Monitor }] : []),
    { name: "Analytics", href: "/metrics", icon: BarChart3 },
    ...(isAdmin ? [{ name: "Team", href: "/team", icon: Users }] : []),
    ...(isAdmin ? [{ name: "Branches", href: "/branches", icon: Building }] : []),
    ...(isAdmin ? [{ name: "Billing", href: "/billing", icon: CreditCard }] : []),
    { name: "Settings", href: "/settings", icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (href) => location.pathname.startsWith(href);

  return (
    <div className="min-h-screen bg-background">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between sticky top-0 z-[100]">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">
              You are viewing as: <strong>{tenant?.company_name}</strong> ({tenant?.subdomain}.aftersales.pro)
            </span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={exitImpersonation}
            className="bg-black text-white hover:bg-black/80"
          >
            Exit Preview
          </Button>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform md:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${isImpersonating ? "top-10" : ""}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg tracking-tight">
                aftersales<span className="text-primary">.pro</span>
              </span>
            </div>
            <button
              className="md:hidden p-2 hover:bg-muted rounded-lg"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  navigate(item.href);
                  setSidebarOpen(false);
                }}
                className={`nav-item w-full ${isActive(item.href) ? "active" : ""}`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </button>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-primary font-medium">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="md:pl-64">
        {/* Top bar */}
        <header className={`glass sticky ${isImpersonating ? "top-10" : "top-0"} z-30 h-16 flex items-center justify-between px-4 md:px-6 gap-4`}>
          <div className="flex items-center gap-4 flex-shrink-0">
            <button
              className="md:hidden p-2 hover:bg-muted rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden lg:block">
              <p className="text-sm font-medium">{tenant?.company_name}</p>
              <p className="text-xs text-muted-foreground">{tenant?.subdomain}.aftersales.pro</p>
            </div>
          </div>

          {/* Universal Search Bar */}
          <div className="flex-1 max-w-xl">
            <UniversalSearch />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSelector variant="ghost" />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === "light" ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="hidden sm:flex" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-2">
                    <span className="text-primary text-sm font-medium">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
